// frontend/src/pages/supplierPlanningColumns.js
//
// The Supplier setup grid's five PLANNING-OVERRIDE columns, and the rule that
// keeps a row consistent while the merchant edits them:
//
//   inventory_target_mode   supplier_sla_lead_time   review_frequency
//   target_days_of_supply                            preferred_order_day
//
// Each is a supplier-level override in a Store → Supplier chain
// (api.services.planning_defaults): NULL on the wire means "inherit the
// store's value", and every row also carries the resolved `effective_*`
// reading. The cell shows the EFFECTIVE value — muted while inherited, with
// the user ink once the row holds its own — the same idiom the safety-stock
// column uses for engine-vs-merchant (utils/safetyStockColDef.js), because
// the merchant's question is the same: "is this number mine, or the
// default?".
//
// Extracted from the page so the rules can be pinned without rendering an
// AG Grid (supplierPlanningColumns.test.js), like itemSetupLinks.
//
// The inherit sentinel and the paste parser are shared with the Item setup
// grid one level down (utils/planningInherit.js).
//
// Days of supply is ALWAYS editable and muted while the row's EFFECTIVE mode
// is Safety stock — mirroring the Store settings card. The value is stored
// either way and is what a flip to Days of supply will read, so a merchant
// may set it ahead of the switch; the hover says it is not in use.

import InheritedCellRenderer from '../components/grid/InheritedCellRenderer';
import { dayOfWeekPickerColDef } from '../utils/dayOfWeekPickerColDef';
import {
  INVENTORY_TARGET_MODES,
  isValidTargetDaysOfSupply,
  readsDaysOfSupply,
} from '../utils/inventoryTargetMode';
import { isValidLeadTimeDays } from '../utils/leadTime';
import { parseClearableUnitsCell } from '../utils/planningCellParsers';
import { INHERIT, modeLabel, parseChoice } from '../utils/planningInherit';
import {
  REVIEW_FREQUENCIES,
  orderDayForCadence,
  requiresPreferredOrderDay,
} from '../constants/planning';
import { TYPEAHEAD_LIST_PARAMS } from '../utils/referencePickerColDef';

const EM_DASH = '—';

// The five override fields, in the order the effective values are re-derived.
export const PLANNING_OVERRIDE_FIELDS = [
  'inventory_target_mode',
  'target_days_of_supply',
  'supplier_sla_lead_time',
  'review_frequency',
  'preferred_order_day',
];

export const frequencyLabel = (t, frequency) =>
  t(`supplierSetup.frequency.${frequency.toLowerCase()}`);
const inheritLabel = (t, storeValueLabel) =>
  t('common.storeSetting', { value: storeValueLabel });

/**
 * Re-derive a row's five `effective_*` readings from its overrides and the
 * store's planning defaults — what the serializer will answer once the row is
 * saved, applied now so a dependent cell (Days of supply's editability, the
 * order day under an inherited cadence) follows an edit in the same render.
 *
 * The cadence pair resolves as ONE override: an inheriting frequency takes
 * the store's day too, whatever the row's own day column holds.
 */
export function resolveEffective(row, planning) {
  row.effective_inventory_target_mode = row.inventory_target_mode === null
    ? planning.inventory_target_mode : row.inventory_target_mode;
  row.effective_target_days_of_supply = row.target_days_of_supply === null
    ? planning.target_days_of_supply : row.target_days_of_supply;
  row.effective_supplier_sla_lead_time = row.supplier_sla_lead_time === null
    ? planning.lead_time_days : row.supplier_sla_lead_time;
  const inheritsCadence = row.review_frequency === null;
  row.effective_review_frequency = inheritsCadence
    ? planning.review_frequency : row.review_frequency;
  row.effective_preferred_order_day = inheritsCadence
    ? planning.preferred_order_day : row.preferred_order_day;
  return row;
}

/**
 * Apply one planning-cell edit's CONSEQUENCES to the row — the paired day a
 * cadence change carries (constants/planning.orderDayForCadence) and the
 * refreshed `effective_*` readings — and return the follow-on cell changes
 * the page must record so they save and highlight alongside the edit.
 *
 * The cell the merchant edited is already written by AG Grid; this handles
 * what that edit implies. Inheriting or Daily drops the day; an anchored
 * cadence keeps a day already chosen and falls to Monday when there is none.
 */
export function applyPlanningEdit({ row, field, newValue, planning }) {
  const changes = [];
  if (field === 'review_frequency') {
    const day = orderDayForCadence(newValue, row.preferred_order_day);
    if (day !== row.preferred_order_day) {
      changes.push({ fieldName: 'preferred_order_day', oldValue: row.preferred_order_day, newValue: day });
      row.preferred_order_day = day;
    }
  }
  resolveEffective(row, planning);
  return changes;
}

// The hover, in the item-setup safety-stock idiom (utils/safetyStockColDef):
// the value the cell falls back to, then what a click or a clear will do.
// `howToInherit` names the gesture — a number cell is cleared, a select is
// put back on its inherit option.
const overrideTooltip = (t, storeValueLabel, overridden, howToInherit) =>
  `${t('common.storeSetting', { value: storeValueLabel })}\n${
    overridden ? t(howToInherit) : t('forecast.clickToOverride')}`;
const CLEAR = 'supplierSetup.clearToInherit';
const CHOOSE_INHERIT = 'supplierSetup.chooseInheritOption';

/**
 * The order-day hover. The day column has NO inherit option of its own — it is
 * half of the cadence override, so the gesture that returns it to the store's
 * day is on the Review frequency column, and a row that inherits cannot be
 * clicked into an override at all. A Daily cadence carries no day, so the
 * hover says so rather than naming one.
 */
const orderDayTooltip = (t, storeDayLabel, row) => {
  const lines = [
    t('common.storeSetting', { value: storeDayLabel }),
    row.review_frequency === null
      ? t('supplierSetup.orderDayFollowsFrequency')
      : t('supplierSetup.orderDayInheritViaFrequency'),
  ];
  if (!requiresPreferredOrderDay(row.effective_review_frequency)) {
    lines.push(t('supplierSetup.orderDayNotUsedDaily'));
  }
  return lines.join('\n');
};

/**
 * @param {object} ctx
 * @param {Function} ctx.t
 * @param {(value: number, dp: number) => string} ctx.formatNumber
 * @param {(raw: string) => number} ctx.parseNumber  locale-aware parser.
 * @param {(dayIndex: number, reviewFrequency?: string) => string} ctx.dayLabel
 * @param {object|null} ctx.planning  the store's defaults
 *   (hooks/useStorePlanning); null while unknown, which leaves all five
 *   columns read-only — an inherit option cannot be offered without naming
 *   what it inherits.
 * @returns {{inventoryTarget: object[], leadTime: object[], orderCadence: object[]}}
 *   The three override groups in grid order — inventory target pair, then
 *   Lead time, then the order cadence pair; the page spreads them in that
 *   sequence.
 */
export function supplierPlanningColumns({
  t, formatNumber, parseNumber, dayLabel, planning,
}) {
  const known = planning !== null;
  const modeOf = (mode) => modeLabel(t, mode);
  const frequencyOf = (frequency) => frequencyLabel(t, frequency);
  const inheritMode = known ? inheritLabel(t, modeOf(planning.inventory_target_mode)) : '';
  const inheritFrequency = known ? inheritLabel(t, frequencyOf(planning.review_frequency)) : '';
  const storeDays = known ? formatNumber(planning.target_days_of_supply, 0) : '';
  const storeLeadTime = known ? formatNumber(planning.lead_time_days, 0) : '';
  const storeDay = known
    ? (planning.preferred_order_day === null
      ? EM_DASH : dayLabel(planning.preferred_order_day, planning.review_frequency))
    : '';

  const overridden = (field) => (row) => row[field] !== null;
  const daysNotUsed = (row) => !readsDaysOfSupply(row.effective_inventory_target_mode);
  // The user ink is the override marker (InheritedCellRenderer); the only cell
  // class is the dirty tint.

  const choiceColumn = ({ field, codes, labelOf, inheritOptionLabel, effectiveField }) => ({
    field,
    editable: () => known,
    cellEditor: 'agRichSelectCellEditor',
    cellEditorParams: {
      values: [INHERIT, ...codes],
      formatValue: (value) => (value === INHERIT || value === null ? inheritOptionLabel : labelOf(value)),
      ...TYPEAHEAD_LIST_PARAMS,
    },
    valueSetter: (params) => {
      const parsed = parseChoice(params.newValue, codes, labelOf, inheritOptionLabel);
      if (parsed === undefined) return false;
      params.data[field] = parsed;
      return true;
    },
    cellRenderer: InheritedCellRenderer,
    cellRendererParams: {
      formatValue: labelOf,
      effectiveValue: (row) => row[effectiveField],
      isOverridden: overridden(field),
    },
  });

  const inventoryTarget = [
    {
      ...choiceColumn({
        field: 'inventory_target_mode',
        codes: INVENTORY_TARGET_MODES,
        labelOf: modeOf,
        inheritOptionLabel: inheritMode,
        effectiveField: 'effective_inventory_target_mode',
      }),
      headerName: t('supplierSetup.columns.inventoryTargetMode'),
      headerTooltip: t('supplierSetup.tooltips.inventoryTargetMode'),
      tooltipValueGetter: (params) => (known && params.data
        ? overrideTooltip(t, modeOf(planning.inventory_target_mode),
          overridden('inventory_target_mode')(params.data), CHOOSE_INHERIT)
        : ''),
    },
    {
      field: 'target_days_of_supply',
      headerName: t('supplierSetup.columns.daysOfSupply'),
      headerTooltip: t('common.tooltips.daysOfSupply'),
      type: 'numberColumn',
      editable: () => known,
      // Blank clears the override (back to the store's days); a number outside
      // 1–365 is refused in place rather than round-tripped to a 400.
      valueParser: (params) => {
        const parsed = parseClearableUnitsCell(params, parseNumber);
        if (parsed === null) return null;
        return isValidTargetDaysOfSupply(String(parsed)) ? parsed : params.oldValue;
      },
      cellRenderer: InheritedCellRenderer,
      cellRendererParams: {
        formatValue: (value) => formatNumber(value, 0),
        effectiveValue: (row) => row.effective_target_days_of_supply,
        isOverridden: overridden('target_days_of_supply'),
      },
      tooltipValueGetter: (params) => (known && params.data
        ? overrideTooltip(t, storeDays, overridden('target_days_of_supply')(params.data), CLEAR)
          + (daysNotUsed(params.data) ? `\n${t('common.daysOfSupplyNotRead')}` : '')
        : ''),
      cellClassRules: {
        'ag-cell-not-in-use': (params) => !!params.data && daysNotUsed(params.data),
      },
    },
  ];
  const leadTime = [
    {
      field: 'supplier_sla_lead_time',
      // The i18n value already carries "(days)" — appending common.days
      // again rendered "Lead time (days) (days)".
      headerName: t('supplierSetup.columns.leadTime'),
      headerTooltip: t('common.tooltips.leadTime'),
      type: 'numberColumn',
      editable: () => known,
      // Blank clears the override (back to the store's lead time); a number
      // outside 0–365 is refused in place rather than round-tripped to a 400.
      // 0 IS a value — same-day supply (utils/leadTime.js).
      valueParser: (params) => {
        const parsed = parseClearableUnitsCell(params, parseNumber);
        if (parsed === null) return null;
        return isValidLeadTimeDays(String(parsed)) ? parsed : params.oldValue;
      },
      cellRenderer: InheritedCellRenderer,
      cellRendererParams: {
        formatValue: (value) => formatNumber(value, 0),
        effectiveValue: (row) => row.effective_supplier_sla_lead_time,
        isOverridden: overridden('supplier_sla_lead_time'),
      },
      tooltipValueGetter: (params) => (known && params.data
        ? overrideTooltip(t, storeLeadTime, overridden('supplier_sla_lead_time')(params.data), CLEAR)
        : ''),
    },
  ];
  const orderCadence = [
    {
      ...choiceColumn({
        field: 'review_frequency',
        codes: REVIEW_FREQUENCIES,
        labelOf: frequencyOf,
        inheritOptionLabel: inheritFrequency,
        effectiveField: 'effective_review_frequency',
      }),
      headerName: t('supplierSetup.columns.reviewFrequency'),
      headerTooltip: t('supplierSetup.tooltips.reviewFrequency'),
      tooltipValueGetter: (params) => (known && params.data
        ? overrideTooltip(t, frequencyOf(planning.review_frequency),
          overridden('review_frequency')(params.data), CHOOSE_INHERIT)
        : ''),
    },
    {
      // Shared day-of-week picker (same editor Data Health used). Row-aware:
      // a monthly supplier's stored Monday MEANS "first Monday". The label
      // reads the EFFECTIVE cadence so an inherited day is named under the
      // store's cadence; `row` is absent when the parser asks for the plain
      // name to match a pasted value against.
      ...dayOfWeekPickerColDef({
        field: 'preferred_order_day',
        formatDay: (d, row) => dayLabel(d, row?.effective_review_frequency),
        // Only a row with its OWN anchored cadence carries a day of its own;
        // an inheriting row shows the store's day, read-only.
        editable: (params) => known && requiresPreferredOrderDay(params.data?.review_frequency),
      }),
      headerName: t('supplierSetup.columns.preferredOrderDay'),
      headerTooltip: t('supplierSetup.tooltips.preferredOrderDay'),
      cellRenderer: InheritedCellRenderer,
      cellRendererParams: {
        formatValue: (value, row) => dayLabel(value, row.effective_review_frequency),
        effectiveValue: (row) => row.effective_preferred_order_day,
        // The day is half of the cadence override, so it is "the row's own"
        // exactly when the frequency is.
        isOverridden: overridden('review_frequency'),
      },
      tooltipValueGetter: (params) => (known && params.data
        ? orderDayTooltip(t, storeDay, params.data)
        : ''),
    },
  ];
  return { inventoryTarget, leadTime, orderCadence };
}

/**
 * The Add-supplier dialog's planning fields → the five wire keys.
 *
 * Form encoding: the two selects hold INHERIT or a code (MUI's select cannot
 * hold null); the days and lead-time boxes hold the typed digits or ''; the
 * day holds 0..6 or null. Each becomes null on the wire when inherited, and
 * the day follows the cadence through orderDayForCadence — the same pairing
 * the grid applies.
 */
export function addSupplierPlanningPayload(form, parseNumber) {
  const reviewFrequency = form.reviewFrequency === INHERIT ? null : form.reviewFrequency;
  return {
    inventory_target_mode: form.inventoryTargetMode === INHERIT ? null : form.inventoryTargetMode,
    target_days_of_supply: form.targetDaysOfSupply === ''
      ? null : Math.round(parseNumber(form.targetDaysOfSupply)),
    supplier_sla_lead_time: form.leadTime === '' ? null : Math.round(parseNumber(form.leadTime)),
    review_frequency: reviewFrequency,
    preferred_order_day: orderDayForCadence(reviewFrequency, form.preferredOrderDay),
  };
}

/**
 * The dialog form's effective mode and cadence — the store's value wherever
 * the form inherits — for the controls that depend on them: the days box is
 * enabled under an effective Days-of-supply mode, the day picker shows under
 * an effective anchored cadence, and the schedule preview restates the pair.
 */
export function effectivePlanningOf(form, planning) {
  const inheritsCadence = form.reviewFrequency === INHERIT;
  return {
    inventory_target_mode: form.inventoryTargetMode === INHERIT
      ? planning.inventory_target_mode : form.inventoryTargetMode,
    review_frequency: inheritsCadence ? planning.review_frequency : form.reviewFrequency,
    preferred_order_day: inheritsCadence ? planning.preferred_order_day : form.preferredOrderDay,
  };
}
